import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, useFormikContext } from 'formik';
import SuperPaymentForm from '../../components/SuperPaymentForm';
import * as Yup from 'yup';
import FormTextField from '../../components/FormTextField';
import { useAppSelector } from '../../app/hooks';
import {
  OrderingConstants,
  OrderingHooks,
  OrderingSelectors,
  OrderingUtils,
} from 'polygon-ordering';
import PaymentProvider from '../../components/PaymentProvider';
import { TyroProvider } from '../../hooks/TyroProvider';
import { RedcatPaymentProvider } from '../../shared/redcatpayments/RedcatPaymentProvider';
import MemberMoneyRedcatPayTopUp from '../../components/MemberMoneyRedcatPayTopUp';
import { Show } from '../../components/atoms/Show';
import { Redirect, Route, Switch } from 'react-router-dom';
import StandardButton from '../../components/StandardButton';
import LoyaltyPaymentScreen from '../LoyaltyPaymentScreen';
import history from '../../history';
import {
  MEMBER_MONEY_ROUTE,
  MEMBER_MONEY_FIELDS_ROUTE,
  MEMBER_MONEY_PAYMENT_ROUTE,
} from '../../constants/loyaltyRoutes';
import type { TopUpLimits } from '../../utils/topUpLimits';
import {
  effectiveMinTopUpCents,
  effectiveMaxTopUpCents,
  isAboveMinTopUp,
  isWithinMaxTopUp,
  cardsetHasTopUpLimits,
} from '../../utils/topUpLimits';

export { MEMBER_MONEY_ROUTE };

const { getGatewayType, getMember, getMemberCardset } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES } = OrderingConstants;
const { useFormattedCurrency } = OrderingHooks;
const { dollarsToCents } = OrderingUtils;

interface MemberMoneyFormValues {
  Amount: string;
}

const MemberMoneyForm = ({
  validationSchema,
  children,
}: {
  validationSchema: any;
  children: React.ReactNode;
}) => (
  <div style={styles.container}>
    <Formik
      validationSchema={validationSchema}
      initialValues={{ Amount: '' }}
      onSubmit={() => {}}
      validateOnMount={true}
    >
      {({ handleSubmit }) => <Form onSubmit={handleSubmit}>{children}</Form>}
    </Formik>
  </div>
);

const MemberMoneyFields = () => {
  const { t } = useTranslation();
  const { handleChange, isValid } = useFormikContext<MemberMoneyFormValues>();
  const gatewayType = useAppSelector(getGatewayType);

  return (
    <div>
      <FormTextField
        onChange={handleChange}
        name="Amount"
        type="number"
        required={true}
        label={t('placeholder.giftcardAmount')}
        slotProps={{
          htmlInput: {
            min: 1,
          },
        }}
      />
      <Show when={gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <div style={styles.buttonContainer}>
          <StandardButton
            label={t('button.proceedToPayment')}
            disabled={!isValid}
            onClick={() => history.push(MEMBER_MONEY_PAYMENT_ROUTE)}
            themeKey="loyaltyButton"
            containerStyle={styles.button}
          />
        </div>
      </Show>
      <Show when={gatewayType !== PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <SuperPaymentForm route="memberCashTopUp" />
      </Show>
    </div>
  );
};

const MemberMoneyScreen = () => {
  const { t } = useTranslation();
  const gatewayType = useAppSelector(getGatewayType);
  const member = useAppSelector(getMember);
  const cardset = useAppSelector(getMemberCardset);

  const enableReCaptchaForMemberTopUp = useAppSelector(
    (state: any) => state.config.enableReCaptchaForMemberTopUp,
  );
  const reCaptchaSiteKey = useAppSelector((state: any) => state.config.reCaptchaSiteKey);

  const isLegacyPaymentGateway = (
    [PAYMENT_GATEWAY_TYPES.STRIPE, PAYMENT_GATEWAY_TYPES.TYRO] as PaymentGatewayType[]
  ).includes(gatewayType);

  let PaymentProviderWrapper: any;
  if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
    PaymentProviderWrapper = PaymentProvider;
  } else if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
    PaymentProviderWrapper = TyroProvider;
  }

  // Mirrors the backend checks in web_ordering.py/member.py: amount can't exceed the
  // cardset's single-deposit cap, nor push the member's balance outside the cardset's
  // min/max balance. member.moneyBalance is already in cents.
  const topUpLimits: TopUpLimits | undefined =
    cardset && member && cardsetHasTopUpLimits(cardset)
      ? {
          minTopupCents: dollarsToCents(cardset.minTopup),
          maxTopupCents: dollarsToCents(cardset.maxTopup),
          minMoneyBalanceCents: dollarsToCents(cardset.minMoneyBalance),
          maxMoneyBalanceCents: dollarsToCents(cardset.maxMoneyBalance),
          moneyCents: member.moneyBalance,
        }
      : undefined;

  const formattedMinTopUp = useFormattedCurrency({
    cents: topUpLimits ? effectiveMinTopUpCents(topUpLimits) : undefined,
    hideZeroCents: true,
  });

  const formattedMaxTopUp = useFormattedCurrency({
    cents: topUpLimits ? effectiveMaxTopUpCents(topUpLimits) : undefined,
    hideZeroCents: true,
  });

  const validationSchema = Yup.object().shape({
    Amount: Yup.number()
      .required(t('form.required'))
      .min(1)
      .test('is-above-min-topup', '', function (value) {
        if (!topUpLimits) return true;
        if (value === undefined || value === null) return true;
        if (isAboveMinTopUp(value, topUpLimits)) return true;
        return this.createError({
          message: t('minTopUpAmount', { amount: formattedMinTopUp }),
        });
      })
      .test('is-within-max-topup', '', function (value) {
        if (!topUpLimits) return true;
        if (value === undefined || value === null) return true;
        if (isWithinMaxTopUp(value, topUpLimits)) return true;
        return this.createError({
          message: t('maxTopUpAmount', { amount: formattedMaxTopUp }),
        });
      }),
  });

  if (!isLegacyPaymentGateway) {
    return (
      <RedcatPaymentProvider
        requireRecaptcha={enableReCaptchaForMemberTopUp}
        recaptchaSiteKey={reCaptchaSiteKey}
        purchaseType="member_money_topup"
      >
        <MemberMoneyForm validationSchema={validationSchema}>
          <Switch>
            <Redirect exact from={MEMBER_MONEY_ROUTE} to={MEMBER_MONEY_FIELDS_ROUTE} />
            <Route path={MEMBER_MONEY_FIELDS_ROUTE}>
              <MemberMoneyFields />
            </Route>
            <Route path={MEMBER_MONEY_PAYMENT_ROUTE}>
              <LoyaltyPaymentScreen>
                <MemberMoneyRedcatPayTopUp />
              </LoyaltyPaymentScreen>
            </Route>
          </Switch>
        </MemberMoneyForm>
      </RedcatPaymentProvider>
    );
  }

  if (isLegacyPaymentGateway) {
    return (
      <PaymentProviderWrapper>
        <MemberMoneyForm validationSchema={validationSchema}>
          <MemberMoneyFields />
        </MemberMoneyForm>
      </PaymentProviderWrapper>
    );
  }

  return <></>;
};

const styles: Styles = {
  container: {
    paddingTop: 32,
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
};

export default MemberMoneyScreen;
